/**
 * Glue between `ParameterField` and the `FieldFork` popover. Owns the
 * token-source / function-template wiring so it only fetches that data when
 * the popover is actually open — mounting one per parameter would otherwise
 * compute token sources for every field in the panel.
 *
 * Reads its position + initial tab from the `fieldFork` slice on
 * `nodeEditorStore`. Writes back via:
 *   - `setFieldForkValueSource` when the user switches tabs
 *   - `closeFieldFork`           when the user dismisses (Esc / outside / ×)
 *   - `onChange`                 when a token / expression is committed
 *
 * Token insertion appends a token segment; expression insertion appends a
 * literal segment wrapped in `${...}`. The popover stays open after a commit;
 * only Esc, outside-click, or the × button closes it.
 */

import { useCallback, useMemo } from 'react';
import { FieldFork } from '../popovers/FieldFork';
import { FunctionPickerPanel } from '../expanded/FunctionPickerPanel';
import { WorkflowInputPanel } from '../expanded/WorkflowInputPanel';
import { wrapAsExpression } from '@/utils/expressions';
import {
  useNodeEditorStore,
  type FieldForkAnchor,
  type ValueSource,
} from '@/stores/nodeEditorStore';
import { useTokenSources, isConnectedToTrigger } from '@/hooks/useTokenSources';
import { useUpstreamManifests } from '@/hooks/useUpstreamManifests';
import { usePreviewOutputs } from '@/hooks/useNodePreviewData';
import { useWorkflowStore } from '@/stores/workflowStore';
import { useAppLevelParameters } from '@/hooks/useAppLevelParameters';
import {
  ValueSegmentType,
  generateSegmentId,
  type Token,
  type ValueSegment,
} from '@/types/valueSegment';
import type { ParameterInfo } from '@/types/operationParameter';
import { telemetryService } from '@/services/telemetryService';
import { useCopyWithToast } from '@/hooks/useCopyWithToast';
import { useApplyTokenTransforms } from '@/hooks/useApplyTokenTransforms';

export interface FieldForkContainerProps {
  parameter: ParameterInfo;
  value: ValueSegment[];
  onChange: (next: ValueSegment[]) => void;
  anchor: FieldForkAnchor;
  initialSource: ValueSource;
  /**
   * What clicking a row inside the popover should do.
   *
   * - `'insert'` (default): commit the token / wrapped expression into
   *   the parameter value via `onChange`. Used for editors whose value
   *   is a flat segment array we can safely append to (StringField,
   *   TextAreaField, NumberField, DictionaryField, ArrayField).
   * - `'browse'`: surface the value to the user without mutating the
   *   parameter — row click writes to the clipboard and dispatches a
   *   "Copied" notification toast. Used for composite editors
   *   (Authentication, Condition, InitializeVariable, Recurrence),
   *   pickers (Combobox, Dropdown, FilePicker, etc.), toggles, and
   *   rich/code editors where appending a literal segment to the
   *   structured value would corrupt it. The user pastes the value
   *   into the inner sub-field they need.
   */
  commitMode?: 'insert' | 'browse';
}

/** Map a `Token`'s source-section onto the telemetry `source_kind` taxonomy. */
function classifyTokenSourceKind(
  token: Token,
): 'trigger' | 'action' | 'variable' | 'foreach' | 'agent_param' {
  const tokenType = (token.tokenType ?? '').toString().toLowerCase();
  if (tokenType.includes('variable')) return 'variable';
  if (tokenType.includes('agentparameter')) return 'agent_param';
  if (tokenType.includes('item')) return 'foreach';
  if (tokenType.includes('trigger')) return 'trigger';
  return 'action';
}

export const FieldForkContainer = ({
  parameter: _parameter,
  value,
  onChange,
  anchor,
  initialSource,
  commitMode = 'insert',
}: FieldForkContainerProps) => {
  const node = useNodeEditorStore((s) => s.node);
  const setFieldForkValueSource = useNodeEditorStore(
    (s) => s.setFieldForkValueSource,
  );
  const closeFieldFork = useNodeEditorStore((s) => s.closeFieldFork);

  const actions = useWorkflowStore(
    (state) => state.workflow?.definition?.actions ?? {},
  );
  const includeTrigger = useMemo(
    () => isConnectedToTrigger(node?.id, actions, node?.parentAgentName),
    [node?.id, actions, node?.parentAgentName],
  );

  // Pull the same upstream manifest set ExpandedView uses so the fork's
  // dynamic-content picker matches the rail.
  const { manifests: upstreamManifests, swaggers: upstreamSwaggers } =
    useUpstreamManifests(node?.id);

  const agentParameterSchema = useMemo(() => {
    if (node?.toolData?.agentParameterSchema) {
      return node.toolData.agentParameterSchema as Record<string, unknown>;
    }
    return undefined;
  }, [node?.toolData?.agentParameterSchema]);

  // App-level parameters from `parameters.json` — surfaced as `@parameters('name')`
  // tokens alongside any declared in the workflow definition itself.
  const appLevelParameters = useAppLevelParameters();

  const { sources: tokenSources } = useTokenSources({
    currentNodeId: node?.id,
    parentAgentName: node?.parentAgentName,
    scopeContext: node?.scopeContext,
    includeTrigger,
    includeParameters: true,
    includeVariables: true,
    includeAgentParameters: !!node?.parentAgentName,
    manifests: upstreamManifests,
    swaggers: upstreamSwaggers,
    agentParameterSchema,
    ...(appLevelParameters ? { appLevelParameters } : {}),
  });

  // Pull the same run-data preview outputs the inline rail consumes so
  // chevrons light up for tokens whose children resolve only after a run.
  const {
    outputs: previewOutputs,
    variables: variableOutputs,
    hasRunData: hasPreviewData,
    isLoading: isLoadingPreviewData,
  } = usePreviewOutputs(node?.id ?? null);

  // Combine action outputs with variable values for expression evaluation
  // — same shape the rail uses, including the special `__variables__`
  // key TokenizedInput reads.
  const combinedPreviewOutputs = useMemo(() => {
    return {
      ...previewOutputs,
      __variables__: variableOutputs,
    };
  }, [previewOutputs, variableOutputs]);

  const isBrowseMode = commitMode === 'browse';

  /**
   * Surface a "Copied to clipboard" / "Copy failed" toast in browse mode.
   * Used as a louder backstop to the WorkflowInputPanel's inline badge for
   * the token tab, and as the *only* feedback path for the expression
   * tab (which has no inline badge of its own). Always performs its own
   * clipboard write so the toast reflects the true success/failure of
   * *this* write — the panel's row-click write happens independently
   * after `onTokenClick` returns, so we can't piggyback on its result.
   * Writing the same string twice is harmless.
   */
  const surfaceCopyToast = useCopyWithToast();
  const applyTokenTransforms = useApplyTokenTransforms();

  // Append a token segment to the field's value, or (in browse mode) surface
  // it as a toast so the user can paste it into a composite editor's inner
  // sub-field. The popover stays open after a commit so users can chain
  // multiple actions — Esc / outside-click / × button explicitly close it.
  const handleTokenSelect = useCallback(
    (token: Token) => {
      if (isBrowseMode) {
        void surfaceCopyToast(token.value ?? '', token.title);
        telemetryService.trackEvent('tokenpicker.fork.token_copied', {
          source_kind: classifyTokenSourceKind(token),
          method: 'click',
        });
        return;
      }

      const resolvedToken = applyTokenTransforms(token);

      const segment: ValueSegment = {
        id: generateSegmentId(),
        type: ValueSegmentType.TOKEN,
        token: resolvedToken,
        value: resolvedToken.value ?? '',
      };
      onChange([...value, segment]);
      telemetryService.trackEvent('tokenpicker.fork.token_inserted', {
        source_kind: classifyTokenSourceKind(resolvedToken),
        method: 'click',
      });
    },
    [isBrowseMode, onChange, value, surfaceCopyToast, applyTokenTransforms],
  );

  // Append an expression template as a literal segment wrapped in ${...}.
  // We always append at the tail (which sits outside any prior expression
  // because segments are flat) — drag-and-drop uses a context-sensitive
  // path in TokenizedInput.handleDrop for mid-expression drops.
  //
  // In browse mode the wrapped template is copied to the clipboard with a
  // success/failure toast — composite editors can't accept a tail-appended
  // literal segment cleanly.
  const handleExpressionInsert = useCallback(
    (template: string) => {
      const wrapped = wrapAsExpression(template);
      const functionCount = (template.match(/\(/g) ?? []).length;
      if (isBrowseMode) {
        void surfaceCopyToast(wrapped, wrapped);
        telemetryService.trackEvent(
          'tokenpicker.fork.expression_copied',
          {},
          { length: template.length, function_count: functionCount },
        );
        return;
      }
      const segment: ValueSegment = {
        id: generateSegmentId(),
        type: ValueSegmentType.LITERAL,
        value: wrapped,
      };
      onChange([...value, segment]);
      telemetryService.trackEvent(
        'tokenpicker.fork.expression_inserted',
        {},
        { length: template.length, function_count: functionCount },
      );
    },
    [isBrowseMode, onChange, value, surfaceCopyToast],
  );

  const handleClose = useCallback(() => {
    // FieldFork doesn't distinguish Esc from outside-click; log a generic
    // dismissal.
    closeFieldFork('outside_click');
  }, [closeFieldFork]);

  const handleSelectSource = useCallback(
    (source: ValueSource) => {
      setFieldForkValueSource(source);
    },
    [setFieldForkValueSource],
  );

  return (
    <FieldFork
      anchor={anchor}
      initialSource={initialSource}
      onClose={handleClose}
      onSelectSource={handleSelectSource}
    >
      {(selected) =>
        selected === 'tokens' ? (
          // Reuse the rail's WorkflowInputPanel so the popover and rail
          // stay visually + functionally identical. `hideTabs` avoids
          // duplicating ParameterValueSourceTabs (FieldFork owns its
          // own). `insertOnRowClick` flips the panel to "click row to
          // commit"; `rowClickAction` selects between mutating the
          // parameter (insert mode) and copying to clipboard (browse
          // mode) since composite/picker editors can't accept a
          // tail-appended segment cleanly.
          <WorkflowInputPanel
            sources={tokenSources}
            onTokenClick={handleTokenSelect}
            hideTabs
            insertOnRowClick
            rowClickAction={isBrowseMode ? 'copy' : 'insert'}
            valueSource="tokens"
            currentNodeId={node?.id}
            actionOutputs={combinedPreviewOutputs}
            hasRunData={hasPreviewData}
            isLoadingRunData={isLoadingPreviewData}
          />
        ) : (
          <FunctionPickerPanel onInsert={handleExpressionInsert} />
        )
      }
    </FieldFork>
  );
};

FieldForkContainer.displayName = 'FieldForkContainer';
